// Pure rules for which filters the panel shows, in what order, and what its
// toggle reads (WCHK-3037). The date range filter is never in the list: it sits
// beside the search field (WCHK-3083).

import moment from "moment-timezone";
import { getDefaultSearchDates } from "@/components/datePicker/Search.dates.util";

// Shared with the toggle's `aria-controls` so the two can't drift.
export const FILTERS_CONTAINER_ID = "nl-search-filters";

export const FILTERS_VIEWS = {
  COLLAPSED: "collapsed",
  // Where a search that mapped filters lands.
  POPULATED: "populated",
  ALL: "all",
};

export const FILTERS_VIEW_VALUES = Object.values(FILTERS_VIEWS);

export const FILTERS_TOGGLE_LABELS = {
  SHOW: "Show Filters",
  SHOW_ALL: "Show All Filters",
  HIDE: "Hide Filters",
};

// Shared with `translateResult` so both agree on "populated".
export const hasValue = (value) =>
  value !== undefined && value !== null && value !== "";

const isPopulated = (filter) => hasValue(filter?.value);

export const hasPopulatedFilters = (filters = []) => filters.some(isPopulated);

/**
 * Whether the date range differs, by day, from the default range.
 * Missing either end counts as unpopulated.
 */
export const isDateRangePopulated = (
  startDate,
  endDate,
  defaults = getDefaultSearchDates()
) =>
  Boolean(startDate && endDate) &&
  !(
    moment(startDate).isSame(defaults.startDate, "day") &&
    moment(endDate).isSame(defaults.endDate, "day")
  );

const byLabel = (a, b) => String(a.label).localeCompare(String(b.label));

// `sort` mutates, and the caller's list is the page's render order.
const sorted = (filters) => [...filters].sort(byLabel);

/**
 * visibleFilters
 *
 * @param {Array} filters - The filters in the panel, date range excluded.
 * @param {string} view - One of FILTERS_VIEWS.
 * @returns {Array} - The visible filters, in render order.
 */
export const visibleFilters = (filters = [], view) => {
  const populated = sorted(filters.filter(isPopulated));
  switch (view) {
    case FILTERS_VIEWS.POPULATED:
      return populated;
    case FILTERS_VIEWS.ALL:
      return [...populated, ...sorted(filters.filter((f) => !isPopulated(f)))];
    default:
      return [];
  }
};

// The populated view with nothing populated stays off the page.
export const isFiltersPanelVisible = (view, populated) =>
  view === FILTERS_VIEWS.ALL ||
  (view === FILTERS_VIEWS.POPULATED && Boolean(populated));

// What the toggle's `aria-expanded` reports.
export const showsEveryFilter = (view) => view === FILTERS_VIEWS.ALL;

export const filtersToggleLabel = (view, populated) => {
  if (view === FILTERS_VIEWS.ALL) return FILTERS_TOGGLE_LABELS.HIDE;
  return view === FILTERS_VIEWS.POPULATED && populated
    ? FILTERS_TOGGLE_LABELS.SHOW_ALL
    : FILTERS_TOGGLE_LABELS.SHOW;
};

// Any view holding filters back expands; the all view collapses.
export const nextFiltersView = (view) =>
  view === FILTERS_VIEWS.ALL ? FILTERS_VIEWS.COLLAPSED : FILTERS_VIEWS.ALL;
